(function () {
  const rootSelector = '[data-custom-carousel]';

  function findSlideByMediaId(viewer, mediaId) {
    return Array.from(viewer.querySelectorAll('.swiper-slide[data-media-id]')).find(
      (slide) => slide.dataset.mediaId === mediaId
    );
  }

  function initCustomCarousel(gallery) {
    if (!window.Swiper || gallery.dataset.customCarouselInitialized === 'true') return;

    const viewer = gallery.querySelector('[data-custom-carousel-main]');
    const thumbs = gallery.querySelector('[data-custom-carousel-thumbs]');
    const nextButton = gallery.querySelector('[data-custom-carousel-next]');
    const prevButton = gallery.querySelector('[data-custom-carousel-prev]');
    const pagination = gallery.querySelector('[data-custom-carousel-pagination]');
    const mediaCount = Number(viewer?.dataset.mediaCount || 0);
    let pendingMediaId = null;

    if (!viewer) return;

    gallery.dataset.customCarouselInitialized = 'true';

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mobileQuery = window.matchMedia('(max-width: 749px)');
    const isMobileLayout = mobileQuery.matches;

    function getSlideImage(slide) {
      return slide?.querySelector('.product__media img, img');
    }

    function markImageEager(slide) {
      const image = getSlideImage(slide);
      if (image) {
        image.loading = 'eager';
        if (image.getAttribute('fetchpriority') !== 'high') image.fetchPriority = 'low';
      }
      return image;
    }

    function waitForSlideImage(slide) {
      const image = markImageEager(slide);
      if (!image || (image.complete && image.naturalWidth > 0)) return Promise.resolve();

      gallery.classList.add('is-loading');

      const decodePromise =
        typeof image.decode === 'function'
          ? image.decode().catch(() => undefined)
          : new Promise((resolve) => {
              image.addEventListener('load', resolve, { once: true });
              image.addEventListener('error', resolve, { once: true });
            });

      const timeoutPromise = new Promise((resolve) => window.setTimeout(resolve, 650));

      return Promise.race([decodePromise, timeoutPromise]).finally(() => {
        gallery.classList.remove('is-loading');
      });
    }

    function preloadNearbySlides() {
      const slides = Array.from(viewer.querySelectorAll('.swiper-slide[data-media-id]'));
      [mainSwiper.activeIndex - 1, mainSwiper.activeIndex, mainSwiper.activeIndex + 1].forEach((index) => {
        if (slides[index]) markImageEager(slides[index]);
      });
    }

    function getOrderedSlides() {
      return Array.from(viewer.querySelectorAll('.swiper-slide[data-media-id]'));
    }

    function syncMobilePaginationPosition() {
      if (!mobileQuery.matches) {
        gallery.style.removeProperty('--custom-carousel-pagination-left');
        gallery.style.removeProperty('--custom-carousel-viewport-width');
        return;
      }

      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      gallery.style.setProperty('--custom-carousel-viewport-width', `${viewportWidth}px`);

      if (!pagination) return;

      const viewerShell = gallery.querySelector('.product-media-custom-carousel__viewer-shell');
      const shellRect = viewerShell?.getBoundingClientRect();
      if (!shellRect) return;

      gallery.style.setProperty(
        '--custom-carousel-pagination-left',
        `${Math.round(viewportWidth / 2 - shellRect.left)}px`
      );
    }

    const swiperOptions = {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: prefersReducedMotion ? 0 : isMobileLayout ? 300 : 460,
      effect: isMobileLayout ? 'slide' : 'fade',
      rewind: mediaCount > 1,
      allowTouchMove: true,
      followFinger: true,
      longSwipesRatio: 0.22,
      resistanceRatio: 0.72,
      threshold: isMobileLayout ? 4 : 0,
      pagination:
        pagination && mediaCount > 1
          ? {
              el: pagination,
              clickable: true,
              bulletClass: 'product-media-custom-carousel__pagination-dot',
              bulletActiveClass: 'product-media-custom-carousel__pagination-dot--active',
            }
          : undefined,
      watchOverflow: true,
      grabCursor: !isMobileLayout && mediaCount > 1,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
    };

    if (!isMobileLayout) {
      swiperOptions.fadeEffect = {
        crossFade: true,
      };
    }

    const mainSwiper = new window.Swiper(viewer, swiperOptions);

    function syncActiveSlide() {
      const activeSlide = viewer.querySelector('.swiper-slide-active[data-media-id]');
      if (!activeSlide) return;

      viewer.querySelectorAll('[data-media-id]').forEach((slide) => slide.classList.remove('is-active'));
      activeSlide.classList.add('is-active');
      syncActiveThumbnail(activeSlide.dataset.mediaId);
      preloadNearbySlides();
      viewer.dispatchEvent(
        new CustomEvent('slideChanged', {
          bubbles: true,
          detail: { currentElement: activeSlide },
        })
      );
    }

    function syncActiveThumbnail(mediaId) {
      if (!thumbs) return;

      thumbs.querySelectorAll('.product-media-custom-carousel__thumb-button').forEach((button) => {
        button.removeAttribute('aria-current');
      });

      const activeThumb = thumbs.querySelector(`[data-target="${mediaId}"]`);
      const activeButton = activeThumb?.querySelector('.product-media-custom-carousel__thumb-button');
      activeButton?.setAttribute('aria-current', 'true');

      if (!activeThumb) return;

      activeThumb.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
    }

    async function slideToMedia(mediaId) {
      const targetSlide = findSlideByMediaId(viewer, mediaId);
      if (!targetSlide) return;

      pendingMediaId = mediaId;
      syncActiveThumbnail(mediaId);
      await waitForSlideImage(targetSlide);
      if (pendingMediaId !== mediaId) return;

      mainSwiper.update();

      const index = getOrderedSlides().indexOf(targetSlide);
      if (index < 0) return;
      if (index === mainSwiper.activeIndex) {
        syncActiveSlide();
        return;
      }
      mainSwiper.slideTo(index, prefersReducedMotion ? 0 : 460);
    }

    function slideByStep(step) {
      const slides = getOrderedSlides();
      if (slides.length < 2) return;

      let nextIndex = mainSwiper.activeIndex + step;
      if (nextIndex >= slides.length) nextIndex = 0;
      if (nextIndex < 0) nextIndex = slides.length - 1;

      slideToMedia(slides[nextIndex].dataset.mediaId);
    }

    function normalizeMediaId(mediaId) {
      if (!mediaId) return null;
      const mediaIdString = String(mediaId);
      if (mediaIdString.startsWith(`${gallery.dataset.sectionId}-`)) return mediaIdString;

      return `${gallery.dataset.sectionId}-${mediaIdString}`;
    }

    function restoreSwiperAfterModal(mediaId) {
      if (mainSwiper.destroyed) return;

      pendingMediaId = null;
      mainSwiper.enable();
      mainSwiper.allowTouchMove = true;
      mainSwiper.allowClick = true;

      if (mainSwiper.touchEventsData) {
        mainSwiper.touchEventsData.isTouched = false;
        mainSwiper.touchEventsData.isMoved = false;
        mainSwiper.touchEventsData.startMoving = false;
      }

      if (mainSwiper.animating) {
        mainSwiper.animating = false;
        mainSwiper.setTransition(0);
      }

      mainSwiper.update();
      syncMobilePaginationPosition();

      const targetMediaId = normalizeMediaId(mediaId);
      if (targetMediaId && findSlideByMediaId(viewer, targetMediaId)) {
        slideToMedia(targetMediaId);
      } else {
        syncActiveSlide();
      }
    }

    function scheduleRestoreAfterModal(mediaId) {
      window.requestAnimationFrame(() => {
        restoreSwiperAfterModal(mediaId);
        window.setTimeout(() => restoreSwiperAfterModal(mediaId), 120);
      });
    }

    mainSwiper.on('slideChange', syncActiveSlide);
    mainSwiper.on('slideChangeTransitionEnd', syncActiveSlide);
    mainSwiper.on('resize', syncMobilePaginationPosition);
    syncMobilePaginationPosition();
    window.requestAnimationFrame(syncMobilePaginationPosition);
    preloadNearbySlides();
    window.addEventListener('resize', syncMobilePaginationPosition);
    window.addEventListener('orientationchange', syncMobilePaginationPosition);
    document.addEventListener('product:media-modal-closed', (event) => {
      if (event?.detail?.sectionId !== gallery.dataset.sectionId) return;
      scheduleRestoreAfterModal(event.detail.mediaId);
    });

    const originalSetActiveMedia = typeof gallery.setActiveMedia === 'function' ? gallery.setActiveMedia.bind(gallery) : null;

    gallery.setActiveMedia = function setActiveMedia(mediaId) {
      originalSetActiveMedia?.(mediaId);
      window.requestAnimationFrame(() => {
        mainSwiper.update();
        slideToMedia(mediaId);
      });
    };

    gallery.addEventListener('click', (event) => {
      const thumbnail = event.target.closest('[data-target]');
      if (!thumbnail || !gallery.contains(thumbnail)) return;
      slideToMedia(thumbnail.dataset.target);
    });

    nextButton?.addEventListener('click', (event) => {
      event.preventDefault();
      slideByStep(1);
    });

    prevButton?.addEventListener('click', (event) => {
      event.preventDefault();
      slideByStep(-1);
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event?.data?.sectionId !== gallery.dataset.sectionId) return;
        const mediaId = event.data.variant?.featured_media?.id;
        if (!mediaId) return;
        slideToMedia(`${gallery.dataset.sectionId}-${mediaId}`);
      });
    }
  }

  function initAllCustomCarousels(scope = document) {
    scope.querySelectorAll(rootSelector).forEach(initCustomCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllCustomCarousels());
  } else {
    initAllCustomCarousels();
  }

  document.addEventListener('shopify:section:load', (event) => initAllCustomCarousels(event.target));
})();
